import React from 'react';

import { Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { SavedRouteAlert } from 'state/emailAlertsSlices';

import { displayFriendlyDayMonthYear } from 'features/common/datetimeUtils';
import { classNames } from 'features/common/utils';
import { secondary_light_text_color } from 'theme/cemTheme';

import {
  formatAlertName,
  formatDayHours,
  formatDayTypes,
  pluralize,
} from './alertHelpers';

import styles from './AlertListItem.module.css';

interface AlertListItemProps {
  alert: SavedRouteAlert;
  onClick?: (alertId: string) => void;
  onEdit?: (alertId: string) => void;
  onDelete?: (alertId: string) => void;
  previewMode?: boolean;
  className?: string;
}

export const AlertListItem = React.memo(function AlertListItem({
  alert,
  onEdit,
  onDelete,
  onClick,
  previewMode = false,
  className,
}: AlertListItemProps) {
  const recipientsCount = alert.recipients.length;
  const routesCount = alert.routes.length;
  const dayTypes = formatDayTypes(alert.schedule_day_types);
  const dayHours = formatDayHours(alert.schedule_day_hours);
  const updatedBy = alert.updated_by;

  return (
    <Card
      key={alert.hash_id}
      variant="outlined"
      className={classNames(styles.alertListItem, className)}
      onClick={() => onClick?.(alert.hash_id)}
    >
      <CardContent>
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color={secondary_light_text_color}>
              {formatAlertName(alert.metric_type, alert.metric_criteria)}
            </Typography>
            <Stack direction="row" spacing={1}>
              {onEdit && (
                <Button
                  variant="text"
                  startIcon={<Edit className={styles.editIcon} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(alert.hash_id);
                  }}
                >
                  <Typography variant="h4" className={styles.buttonText}>
                    Edit
                  </Typography>
                </Button>
              )}
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(alert.hash_id);
                  }}
                >
                  <DeleteIcon className={styles.deleteIcon} />
                </IconButton>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.25} gap={0.5}>
            <Typography variant="caption" className={styles.alertLabel}>
              Day Type
            </Typography>
            {dayTypes.map((day) => (
              <Box key={day} className={styles.chip}>
                <Typography variant="caption">{day}</Typography>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={0.25} flexWrap="wrap" gap={0.5}>
            <Typography variant="caption" className={styles.alertLabel}>
              Day Parts
            </Typography>
            {dayHours.map((dayPart, idx) => (
              <Box key={idx} className={styles.chip}>
                <Typography variant="caption">{dayPart}</Typography>
              </Box>
            ))}
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {previewMode ? (
              <Typography variant="caption" className={styles.alertCaption}>
                {recipientsCount} {pluralize(recipientsCount, ' recipient')}{' '}
                assigned to this alert
              </Typography>
            ) : (
              <>
                <Typography variant="caption" className={styles.alertCaption}>
                  {recipientsCount} {pluralize(recipientsCount, 'Recipient')} |{' '}
                  {routesCount} {pluralize(routesCount, 'Route')}
                </Typography>
                {updatedBy && (
                  <Typography variant="caption" className={styles.alertCaption}>
                    Last updated by {updatedBy} on{' '}
                    {displayFriendlyDayMonthYear(alert.updated_on)}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
});
